import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { saveAs } from "file-saver";
import { DownloadOutlined } from "@ant-design/icons";
import Footer from "../../CoreComponents/Footer";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import { showMessage } from "../../Functionalities/Functions";
import confirmBox from "../../CoreComponents/confirmBox";
import { fmtDate, rupees } from "../Insights/format";
import PaymentsTable from "../DeliveryBoys/PaymentsTable";
import { deletePayment, fetchAllPayments, fetchPayments, paymentsCsv } from "../DeliveryBoys/riderPaymentsData";
import "../DeliveryBoys/riderReport.css";
import Stat from "../Settlements/Stat";
import PaymentsRangeFilter from "./PaymentsRangeFilter";
import { canSettle } from "../Settlements/access";
import "../Settlements/settlements.css";

export const PAGE_SIZE = 50;
const EMPTY = { rows: [], count: 0, total: 0 };

export const pageLabel = (page, count) =>
  count ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, count)} of ${count}` : "No payments";

function Settlements() {
  const [range, setRange] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchPayments({ range, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }).then((result) => {
      if (!live) return;
      setData(result);
      setLoading(false);
      if (result.error) showMessage(result.error, "danger", "Could not load the payments", "top", "top-right");
    });
    return () => {
      live = false;
    };
  }, [range, page, version]);

  const search = (next) => {
    setPage(1);
    setRange(next);
    setVersion((v) => v + 1);
  };

  const download = async () => {
    const rows = await fetchAllPayments(range);
    const name = range ? `rider-settlements-${range.from}-to-${range.to}.csv` : "rider-settlements.csv";
    saveAs(new Blob([paymentsCsv(rows)], { type: "text/csv;charset=utf-8" }), name);
  };

  const remove = (payment) => {
    const who = payment.rider.name || `Rider #${payment.rider.id}`;
    confirmBox({
      title: `Delete ${who}'s payment?`,
      message: `${rupees(payment.amount)} paid on ${fmtDate(payment.paid_on)}. The rider holds that cash again.`,
      yesLabel: "Delete",
      noLabel: "Cancel",
      onYes: () => removeConfirmed(payment),
    });
  };

  const removeConfirmed = async (payment) => {
    const body = await deletePayment(payment.id);
    if (body.Status !== true) {
      showMessage(body.Error || "The payment was not deleted", "danger", "Not deleted", "top", "top-right");
      return;
    }
    setVersion((v) => v + 1);
  };

  const lastPage = Math.max(1, Math.ceil(data.count / PAGE_SIZE));

  return (
    <div className="container-fluid stl">
      <section className="stl-card stl-toolbar">
        <PaymentsRangeFilter applied={range} loading={loading} onSearch={search} />
        <button type="button" className="stl-btn stl-btn--secondary" disabled={!data.count} onClick={download}>
          <DownloadOutlined /> Download CSV
        </button>
      </section>

      <section className="stl-stats">
        <Stat label="Received" value={rupees(data.total)} hint={range ? `${fmtDate(range.from)} to ${fmtDate(range.to)}` : "all time"} />
        <Stat label="Payments" value={data.count} />
      </section>

      <section className="stl-card stl-table-card">
        <div className="stl-table-bar">
          <div className="stl-table-bar__title">
            <h2 className="stl-title">Previous settlements</h2>
            <span className="stl-subtitle">{pageLabel(page, data.count)}</span>
          </div>
          {lastPage > 1 && (
            <div className="stl-table-bar__tools">
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === lastPage} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
        <PaymentsTable rows={data.rows} loading={loading} showRider onDelete={remove} emptyText={data.error || "No rider has paid in this period"} />
      </section>
    </div>
  );
}

export default function DeliveryboyReports() {
  if (!canSettle()) return <Redirect to="/orders" />;
  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name="Previous settlements" subHeading="What each rider paid the office" />
      <NavBar />
      <div className="content-body" style={{ minHeight: "100vh" }}>
        <Settlements />
      </div>
      <Footer />
    </div>
  );
}
